import { Button, IconButton, TabMenu, TabMenuList, TabMenuTrigger } from '@ads/components-react';
import { ArrowPathIcon, PhotoIcon, TrashIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileUpload, ProgressBar, Skeleton, Spinner } from '@/shared/ui/atoms';

import { BANNER_MEDIA_KIND, type BannerMediaKind } from '../../../types/bannerForm';
import { BANNER_ERROR_ANCHORS } from '../../../utils/bannerFormErrors';
import {
  BANNER_PREVIEW_LABEL_VALUES,
  BANNER_VIDEO,
  BANNER_VIDEO_LABEL_VALUES,
  type BannerFileRejection,
  type BannerVideoMetadata,
  fileNameFromUrl,
  formatDurationLabel,
  formatFileSize,
  formatMediaFormat,
  readBannerVideoMetadata,
} from '../../../utils/bannerMedia';
import { BannerFieldLabel } from '../../molecules/BannerFieldLabel';
import { BannerFormSection } from '../../molecules/BannerFormSection';
import { BannerImageField } from '../../molecules/BannerImageField';

export interface BannerMediaSectionProps {
  readonly mediaKind: BannerMediaKind;
  readonly onMediaKindChange: (kind: BannerMediaKind) => void;
  /** Título do banner, exibido como nome da arte já cadastrada. */
  readonly bannerTitle: string;
  /** Arte nova escolhida (ainda não enviada). */
  readonly imageFile: File | null;
  /** Arte já cadastrada, exibida enquanto o operador não troca nem remove. */
  readonly existingImageUrl: string | undefined;
  readonly onImageChange: (file: File | null) => void;
  /** Arte do par legado (`Image`), consumida apenas pelo ambiente antigo. */
  readonly imageDesktopFile: File | null;
  readonly existingImageDesktopUrl: string | undefined;
  readonly onImageDesktopChange: (file: File | null) => void;
  readonly videoFile: File | null;
  /** Vídeo já cadastrado no banner; exibido enquanto não é trocado nem removido. */
  readonly existingVideoUrl: string | undefined;
  readonly videoMetadata: BannerVideoMetadata | null;
  readonly onVideoChange: (metadata: BannerVideoMetadata | null) => void;
  /** Oculta o seletor imagem/vídeo — usado nas abas de tradução. */
  readonly variant?: 'full' | 'translation';
  /**
   * Pendência de mídia vinda da validação do formulário — hoje, o arquivo que
   * falta para substituir a mídia do tipo anterior.
   */
  readonly mediaError?: string | undefined;
  /**
   * Pendência do par **legado** — hoje, a arte que falta para acompanhar a do MBX
   * (ver `bannerFormSchema`).
   */
  readonly imageDesktopError?: string | undefined;
  /** `true` enquanto o formulário envia: os cards mostram o arquivo subindo. */
  readonly isUploading?: boolean;
  /** Bytes enviados (0–100); `null` quando o total é desconhecido. */
  readonly uploadProgress?: number | null;
}

/**
 * Bloco "Mídia" do formulário de Banner.
 *
 * Um banner exibe **imagem ou vídeo, nunca os dois**, e a tela reflete isso: no
 * modo imagem existe um único campo de arte — a resolução maior foi descontinuada
 * —, e no modo vídeo existe só o campo do MP4, lido localmente (duração e
 * resolução vêm do próprio arquivo). Limites e medidas saem de `BANNER_IMAGE`/
 * `BANNER_VIDEO`, interpolados nos textos.
 *
 * O cadastro **não apaga mídia**: enviar um arquivo novo substitui o que está lá,
 * e é só isso que a tela oferece para uma mídia já cadastrada. O "Descartar
 * escolha" aparece apenas sobre um arquivo recém-escolhido, que ainda não subiu —
 * descartar volta para a mídia que está no cadastro.
 */
export function BannerMediaSection({
  mediaKind,
  onMediaKindChange,
  bannerTitle,
  imageFile,
  existingImageUrl,
  onImageChange,
  imageDesktopFile,
  existingImageDesktopUrl,
  onImageDesktopChange,
  videoFile,
  existingVideoUrl,
  videoMetadata,
  onVideoChange,
  variant = 'full',
  mediaError,
  imageDesktopError,
  isUploading = false,
  uploadProgress = null,
}: BannerMediaSectionProps) {
  const { t } = useTranslation('banner');
  const [videoRejection, setVideoRejection] = useState<BannerFileRejection | null>(null);
  const [isReadingVideo, setIsReadingVideo] = useState(false);
  // Miniatura do vídeo cadastrado: até os primeiros frames chegarem, o quadro fica
  // preto e parecia mídia quebrada. Guardar a URL carregada faz a troca de arquivo
  // voltar ao skeleton sozinha.
  const [loadedVideoUrl, setLoadedVideoUrl] = useState<string | undefined>(undefined);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const isVideo = mediaKind === BANNER_MEDIA_KIND.Video;
  // A arte do MBX escolhida sem par legado: o contrato tem dois destinos e o
  // cadastro não apaga mídia, então o legado ficaria com a arte antiga. Só vale
  // para o cadastro em português: a tradução manda o mesmo arquivo nos dois pares.
  const needsLegacyImage =
    variant === 'full' &&
    !isVideo &&
    imageFile !== null &&
    imageDesktopFile === null &&
    existingImageDesktopUrl === undefined;
  const handleVideoPicked = async (files: File[]) => {
    const picked = files[0];
    if (!picked) return;
    setVideoRejection(null);
    setIsReadingVideo(true);
    const result = await readBannerVideoMetadata(picked);
    setIsReadingVideo(false);
    if (!result.ok) {
      setVideoRejection(result.rejection);
      return;
    }
    onVideoChange(result.metadata);
  };

  // Seletor de mídia como `TabMenu` do design system: são duas vistas
  // mutuamente exclusivas do mesmo bloco, que é exatamente o papel de uma aba.
  const mediaToggle = (
    <TabMenu value={mediaKind} onValueChange={value => onMediaKindChange(value as BannerMediaKind)}>
      <TabMenuList>
        <TabMenuTrigger
          value={BANNER_MEDIA_KIND.Image}
          leadingIcon={PhotoIcon}
          disabled={isUploading}
        >
          {t('banner.form.media.image')}
        </TabMenuTrigger>
        <TabMenuTrigger
          value={BANNER_MEDIA_KIND.Video}
          leadingIcon={VideoCameraIcon}
          disabled={isUploading}
        >
          {t('banner.form.media.video')}
        </TabMenuTrigger>
      </TabMenuList>
    </TabMenu>
  );

  return (
    <BannerFormSection
      title={t('banner.form.media.heading')}
      hint={t('banner.form.media.hint', BANNER_PREVIEW_LABEL_VALUES)}
      errorAnchor={BANNER_ERROR_ANCHORS.Media}
      {...(variant === 'full' ? { action: mediaToggle } : {})}
    >
      {isVideo ? (
        <div className="flex w-full flex-col gap-nano">
          <BannerFieldLabel label={t('banner.form.media.videoField')} />

          {videoMetadata && videoFile ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-mini rounded-sm border-hairline border-ctx-layout-border px-micro py-nano">
              <div className="flex flex-col gap-quark">
                <span className="ts-subtitle-xxs text-ctx-content-title">{videoFile.name}</span>
                {/* Durante o envio o que importa é o progresso; fora dele, os dados
                    lidos do próprio arquivo. */}
                {isUploading ? (
                  <span className="flex items-center gap-nano ts-caption-xxs text-ctx-content-base">
                    <span className="shrink-0">{t('banner.form.media.uploading')}</span>
                    {uploadProgress === null ? (
                      <Spinner size="sm" />
                    ) : (
                      <span className="min-w-[5rem] flex-1">
                        <ProgressBar shouldShowPercentage progressPercentage={uploadProgress} />
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="ts-caption-xxs text-ctx-content-base">
                    {t('banner.form.media.videoMeta', {
                      width: videoMetadata.width,
                      height: videoMetadata.height,
                      size: formatFileSize(videoFile.size),
                    })}
                    {` · ${formatDurationLabel(videoMetadata.durationSeconds)}`}
                  </span>
                )}
              </div>
              <IconButton
                icon={TrashIcon}
                ariaLabel={t('banner.form.media.discard')}
                hierarchy="tertiary"
                size="sm"
                disabled={isUploading}
                onClick={() => onVideoChange(null)}
              />
            </div>
          ) : isReadingVideo ? (
            <div className="flex items-center gap-nano rounded-sm border-hairline border-ctx-layout-border px-micro py-nano">
              <Spinner size="sm" />
              <span className="ts-caption-xxs text-ctx-content-base">
                {t('banner.form.media.videoProcessing')}
              </span>
            </div>
          ) : existingVideoUrl ? (
            // Vídeo que já estava no banner: mostra o próprio arquivo (sem
            // controles, como no preview) em vez de um <img> quebrado.
            <div className="flex w-full flex-wrap items-center gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-body p-micro">
              <div className="relative shrink-0">
                <video
                  src={existingVideoUrl}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="h-[7.5rem] w-[7.4375rem] rounded-sm border-hairline border-ctx-layout-border bg-base-gray-800 object-cover"
                  aria-label={t('banner.form.media.videoField')}
                  onLoadedData={() => setLoadedVideoUrl(existingVideoUrl)}
                />

                {loadedVideoUrl === existingVideoUrl ? null : (
                  <Skeleton className="absolute inset-none h-full w-full animate-none" />
                )}

                {isUploading ? (
                  <span className="absolute inset-none flex items-center justify-center rounded-sm bg-base-gray-900 opacity-medium">
                    {uploadProgress === null ? <Spinner size="sm" /> : null}
                  </span>
                ) : null}
              </div>

              <div className="flex min-w-[12rem] flex-1 flex-col gap-nano">
                <div className="flex flex-col gap-quark">
                  <span className="ts-subtitle-xxs text-ctx-content-title">
                    {bannerTitle.trim() || fileNameFromUrl(existingVideoUrl)}
                  </span>
                  {isUploading ? (
                    <span className="flex items-center gap-nano ts-caption-xxs text-ctx-content-base">
                      <span className="shrink-0">{t('banner.form.media.uploading')}</span>
                      {uploadProgress === null ? (
                        <Spinner size="sm" />
                      ) : (
                        <span className="min-w-[5rem] flex-1">
                          <ProgressBar shouldShowPercentage progressPercentage={uploadProgress} />
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="ts-caption-xxs text-ctx-content-base">
                      {formatMediaFormat(existingVideoUrl)}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-nano">
                  <Button
                    type="button"
                    hierarchy="secondary"
                    size="sm"
                    leadingIcon={ArrowPathIcon}
                    disabled={isUploading}
                    onClick={() => videoInputRef.current?.click()}
                  >
                    {t('banner.form.media.replace')}
                  </Button>
                </div>
              </div>

              <input
                ref={videoInputRef}
                type="file"
                accept={BANNER_VIDEO.ACCEPT}
                className="sr-only"
                disabled={isUploading}
                onChange={event => {
                  const files = event.target.files ? Array.from(event.target.files) : [];
                  event.target.value = '';
                  void handleVideoPicked(files);
                }}
              />
            </div>
          ) : (
            <FileUpload
              files={[]}
              addFiles={files => void handleVideoPicked(files)}
              removeFile={() => onVideoChange(null)}
              accept={BANNER_VIDEO.ACCEPT}
              hasError={videoRejection !== null || mediaError !== undefined}
              disabled={isUploading}
            />
          )}

          {videoRejection ? (
            <p className="ts-caption-xxs text-base-danger-700">
              {t(
                videoRejection === 'type'
                  ? 'banner.form.media.errors.videoType'
                  : 'banner.form.media.errors.videoSize',
                BANNER_VIDEO_LABEL_VALUES,
              )}
            </p>
          ) : mediaError !== undefined ? (
            <p className="ts-caption-xxs text-base-danger-700">{mediaError}</p>
          ) : null}
        </div>
      ) : null}

      {/* No modo vídeo não há campo de imagem: a mídia do banner é o vídeo. Em
          imagem há dois destinos no contrato: o par mobile, que é o que o app
          exibe (e o preview espelha), e o par legado, consumido só pelo ambiente
          antigo. */}
      {isVideo ? null : (
        <>
          <BannerImageField
            label={t('banner.form.media.imageMobileField')}
            info={t('banner.form.media.imageMobileHint')}
            bannerTitle={bannerTitle}
            file={imageFile}
            existingUrl={existingImageUrl}
            onChange={onImageChange}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            {...(mediaError !== undefined ? { error: mediaError } : {})}
          />

          <div
            className="flex w-full flex-col gap-nano"
            data-field-error={BANNER_ERROR_ANCHORS.ImageDesktop}
          >
            <BannerImageField
              label={`${t('banner.form.media.imageLegacyField')}${needsLegacyImage ? '*' : ''}`}
              info={t('banner.form.media.imageLegacyNotice')}
              bannerTitle={bannerTitle}
              file={imageDesktopFile}
              existingUrl={existingImageDesktopUrl}
              onChange={onImageDesktopChange}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              {...(imageDesktopError !== undefined ? { error: imageDesktopError } : {})}
            />

            {/* O aviso não espera o submit: assim que a arte do MBX é escolhida, a
                do legado passa a ser obrigatória — o cadastro não apaga mídia, e o
                legado continuaria exibindo a arte antiga. */}
            {needsLegacyImage && imageDesktopError === undefined ? (
              <p className="ts-caption-xxs text-ctx-content-base">
                {t('banner.form.media.imageLegacyRequired')}
              </p>
            ) : null}
          </div>
        </>
      )}
    </BannerFormSection>
  );
}
